import { type AnthropicMessagesSettings } from "./anthropic";
import {
  type AnthropicModel,
  createCrosshatchAnthropic,
  isAnthropicModel,
} from "./anthropic";
import { type OpenAIModel, isOpenAIModel } from "./openai";
import { type TimelineQueryType } from "@crosshatch/db";
import { createCrosshatchOpenAI } from "./openai";
import type {
  EmbeddingModelV1,
  LanguageModelV1,
  ProviderV1,
} from "@ai-sdk/provider";
import type { FetchFunction } from "@ai-sdk/provider-utils";
import type {
  OpenAIChatSettings,
  OpenAIEmbeddingSettings,
} from "@ai-sdk/openai/internal";

export type CrosshatchSettings = {
  /**
   * Base URL for the API.
   * @default 'https://ai.crosshatch.io'
   */
  baseUrl?: string;

  /**
   * Token for authenticating requests.
   */
  token?: string | (() => string);

  /**
   * Replacement parameters for the request.
   */
  replace?: Record<string, TimelineQueryType>;

  /**
   * Custom headers to include in the requests.
   */
  headers?: Record<string, string>;

  /**
   * Custom fetch implementation. You can use it as a middleware to intercept requests,
   * or to provide a custom fetch implementation for e.g. testing.
   */
  fetch?: FetchFunction;
};
type CrosshatchOpenAIChatSettings = OpenAIChatSettings & CrosshatchSettings;
type CrosshatchOpenAIEmbeddingSettings = OpenAIEmbeddingSettings &
  CrosshatchSettings;
// type CrosshatchGoogleVertexSettings = GoogleVertexSettings & CrosshatchSettings;
type CrosshatchAnthropicChatSettings = AnthropicMessagesSettings &
  CrosshatchSettings;

const resolveSettings = ({
  token,
  baseUrl = "https://ai.crosshatch.io",
  ...settings
}: CrosshatchSettings) => ({
  ...settings,
  baseUrl,
  token: typeof token === "function" ? token() : token,
});

export interface CrosshatchProvider extends ProviderV1 {
  (
    deploymentId: OpenAIModel,
    settings?: OpenAIChatSettings & CrosshatchSettings,
  ): LanguageModelV1;
  (
    deploymentId: AnthropicModel,
    settings?: OpenAIChatSettings & CrosshatchSettings,
  ): LanguageModelV1;
  // (deploymentId: VertexModel, settings?: GoogleVertexSettings & CrosshatchSettings): LanguageModelV1;

  /**
Creates a Crosshatch chat model for text generation.
   */
  languageModel(
    deploymentId: OpenAIModel,
    settings?: OpenAIChatSettings,
  ): LanguageModelV1;
  languageModel(
    deploymentId: AnthropicModel,
    settings?: CrosshatchAnthropicChatSettings,
  ): LanguageModelV1;

  /**
Creates a Crosshatch model for text embeddings.
   */
  textEmbeddingModel(
    deploymentId: OpenAIModel,
    settings?: CrosshatchOpenAIEmbeddingSettings,
  ): EmbeddingModelV1<string>;
  textEmbeddingModel(deploymentId: AnthropicModel): EmbeddingModelV1<string>;

  // textEmbeddingModel(
  //   deploymentId: VertexModel,
  // ): EmbeddingModelV1<string>;
}
/**
Create a Crosshatch provider instance.
 */
export function createCrosshatch(options: CrosshatchSettings = {}) {
  const openAI = (settings: CrosshatchSettings) =>
    createCrosshatchOpenAI(resolveSettings({ ...options, ...settings }));
  const anthropic = (settings: CrosshatchSettings) =>
    createCrosshatchAnthropic(resolveSettings({ ...options, ...settings }));

  function createChatModel(
    ...[provider, settings]:
      | [OpenAIModel, CrosshatchOpenAIChatSettings]
      | [AnthropicModel, CrosshatchAnthropicChatSettings]
  ) {
    if (isOpenAIModel(provider)) {
      return openAI(settings).languageModel(provider, settings);
    }
    if (isAnthropicModel(provider)) {
      return anthropic(settings).languageModel(
        provider,
        settings as CrosshatchAnthropicChatSettings,
      );
    }

    throw new Error("Vertex models are not supported");
  }

  const createCompletionModel = (
    ...[provider, settings]:
      | [OpenAIModel, CrosshatchOpenAIChatSettings]
      | [AnthropicModel, CrosshatchAnthropicChatSettings]
  ) => {
    if (isOpenAIModel(provider)) {
      return openAI(settings).languageModel(provider, settings);
    }
    if (isAnthropicModel(provider)) {
      return anthropic(settings).languageModel(
        provider,
        settings as CrosshatchAnthropicChatSettings,
      );
    }

    throw new Error("Vertex models are not supported");
  };

  function createEmbeddingModel(
    ...[provider, settings]:
      | [OpenAIModel, CrosshatchOpenAIEmbeddingSettings]
      | [AnthropicModel, CrosshatchAnthropicChatSettings]
  ) {
    if (isOpenAIModel(provider)) {
      return openAI(settings).textEmbeddingModel(provider, settings);
    }
    if (isAnthropicModel(provider)) {
      return anthropic(settings).textEmbeddingModel(provider);
    }

    throw new Error("Vertex models are not supported");
  }
  const provider = function (
    ...[provider, settings]:
      | [OpenAIModel, CrosshatchOpenAIChatSettings]
      | [AnthropicModel, CrosshatchAnthropicChatSettings]
  ) {
    if (new.target) {
      throw new Error(
        "The Crosshatch OpenAI model function cannot be called with the new keyword.",
      );
    }
    if (isOpenAIModel(provider)) {
      return openAI(settings).textEmbeddingModel(provider, settings);
    }
    if (isAnthropicModel(provider)) {
      return anthropic(settings).textEmbeddingModel(provider);
    }
  };

  provider.languageModel = createChatModel;
  provider.chat = createChatModel;
  provider.completion = createCompletionModel;
  provider.embedding = createEmbeddingModel;
  provider.textEmbedding = createEmbeddingModel;
  provider.textEmbeddingModel = createEmbeddingModel;
  return provider;
}

/**
Default Crosshatch OpenAI provider instance.
 */
export const crosshatch = createCrosshatch();
